import { exists } from '@jkhy/frontend-utils/functions';
import { CompanyModelDto } from '@treasury/api/bo';
import { DomainEntity } from '../../../../shared';
import { ChallengeGroup } from './challenge-group.entity';

const defaultCompany: CompanyModelDto = {
    id: 0,
    cifNumber: '',
    name: '',
    status: '',
    taxNumber: '',
    customerSince: '',
    addedOn: '',
    companyGuidUniqueId: '',
};

export class Company extends DomainEntity<CompanyModelDto> {
    constructor(dto: CompanyModelDto, challengeGroups: ChallengeGroup[] = []) {
        super(dto);
        this.challengeGroups = challengeGroups;
    }

    public challengeGroups: ChallengeGroup[] = [];

    public get cifNumber() {
        return this.dto.cifNumber;
    }

    public get companyId() {
        return this.dto.companyId;
    }

    public get name() {
        return this.dto.name;
    }

    public get taxNumber() {
        return this.dto.taxNumber;
    }

    public get addressLine1() {
        return this.dto.address?.line1 ?? '';
    }

    public get addressLine2() {
        return this.dto.address?.line2 ?? '';
    }

    public get addressLine3() {
        return this.dto.address?.line3 ?? '';
    }

    public get city() {
        return this.dto.address?.city ?? '';
    }

    public get state() {
        return this.dto.address?.state ?? '';
    }

    public get zipCode() {
        return this.dto.address?.zipCode ?? '';
    }

    public get status() {
        return this.dto.status;
    }

    public get id() {
        return this.dto.id;
    }

    public get companyName() {
        return this.dto.name;
    }

    public get formattedComments() {
        const comments = this.dto.comments?.split(/\n|\r/);
        // strip all duplicate strings from the comments array
        return comments?.filter((comment, index, self) => self.indexOf(comment) === index);
    }

    public get comments() {
        return this.dto.comments;
    }

    public set comments(string) {
        this.dto.comments = string;
    }

    public get customerSince() {
        return new Date(this.dto.customerSince);
    }

    public get treasuryCustomerSince() {
        return new Date(this.dto.addedOn);
    }

    public createDefault(): this {
        return new Company(defaultCompany) as this;
    }

    public get authenticationEnabled() {
        return Boolean(this.dto.companyAuthenticationSettings?.isActive);
    }

    public set authenticationEnabled(value: boolean) {
        if (this.dto.companyAuthenticationSettings) {
            this.dto.companyAuthenticationSettings.isActive = value;
        }
    }

    public get authenticationMethod() {
        return this.dto.companyAuthenticationSettings?.challengeMethodType;
    }

    public set authenticationMethod(value) {
        if (this.dto.companyAuthenticationSettings && exists(value)) {
            this.dto.companyAuthenticationSettings.challengeMethodType = value;
        }
    }

    public get authenticationProfileName() {
        return this.dto.companyAuthenticationSettings?.challengeGroup?.name;
    }

    public get authenticationProfileId() {
        return this.dto.companyAuthenticationSettings?.challengeGroup?.id;
    }

    public set authenticationProfile(profileId: number) {
        if (!this.dto.companyAuthenticationSettings) {
            return;
        }

        const profile = this.challengeGroups.find(group => group.id === profileId);
        if (!exists(profile)) {
            return;
        }

        this.dto.companyAuthenticationSettings.challengeGroup = profile;
    }

    public get notOnboarded() {
        return this.dto.status === 'Not Onboarded';
    }
}
